import * as React from 'react'
import {
  Button,
  Typography,
  CircularProgress,
  TextField,
  Grid,
  Box,
  Alert,
} from '@mui/material'
import {
  useForm,
  Controller,
  SubmitHandler,
  useFieldArray,
} from 'react-hook-form'
import { grey } from '@mui/material/colors'
import { useTranslation } from 'react-i18next'
import PercentIcon from '@mui/icons-material/Percent'
import { ReferenceDataValueReq } from 'types'
import { useSnackbar } from 'notistack'
import usePageView from 'hooks/usePageView'
import ReferenceDataService from 'api/services/ReferenceData'

interface ReferenceDataFields {
  referenceData: ReferenceDataValueReq[]
}

export default function CalculatorTools() {
  usePageView()
  const { t } = useTranslation()
  const [, setLocalIsError] = React.useState(false)
  const { enqueueSnackbar } = useSnackbar()

  const handleCloseError = () => {
    setLocalIsError(true)
  }

  const { handleSubmit, control, reset } = useForm<ReferenceDataFields>({
    defaultValues: {
      referenceData: [],
    },
  })

  const { fields } = useFieldArray({
    control,
    name: 'referenceData',
  })

  const onSubmit: SubmitHandler<ReferenceDataFields> = (data) => {
    updateReferenceDataMutation.mutate(data.referenceData)
  }

  const getReferenceData = ReferenceDataService.hooks.useReferenceData({
    onSuccess: (data) => {
      reset({
        referenceData: data,
      })
    },
  })

  const updateReferenceDataMutation =
    ReferenceDataService.hooks.useUpdateReferenceData({
      onSuccess: (data) => {
        enqueueSnackbar(t('dashboard.pages.tools.success'), {
          variant: 'success',
        })
        getReferenceData.refetch()
      },
      onError: (error) => {
        enqueueSnackbar(t('error.unspecific'), {
          variant: 'error',
        })
      },
    })

  if (getReferenceData.isLoading) {
    return (
      <Box sx={{ justifyContent: 'center', display: 'flex' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (getReferenceData.isError) {
    return (
      <Box sx={{ justifyContent: 'center', display: 'flex' }}>
        <Grid container>
          <Grid item xs={12}>
            <Alert severity="error" onClose={handleCloseError}>
              {t('error.unspecific')}
            </Alert>
          </Grid>
        </Grid>
      </Box>
    )
  }

  if (getReferenceData.isSuccess) {
    return (
      <Grid
        container
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        justifyContent="center"
        alignItems="stretch"
        spacing={2}
      >
        <Grid item>
          <Box
            sx={{
              bgcolor: 'background.paper',
              px: {
                xs: '16px',
                sm: '16px',
                md: '24px',
                lg: '32px',
                xl: '48px',
              },
              boxShadow: 25,
              border: '1px solid',
              borderColor: grey[300],
              pb: '16px',
              borderRadius: '9px',
              maxWidth: (theme) => theme.breakpoints.values.sm,
              mx: 'auto',
              p: '32px',
              mt: { xs: '56px', md: '10px' },
            }}
          >
            <Typography
              variant="h4"
              align="center"
              sx={{ fontWeight: 'bolder' }}
            >
              {t('dashboard.pages.tools.title')}
            </Typography>
            <Grid container justifyContent="center" rowSpacing={4}>
              <Grid item xs={12}>
                <Grid container spacing={2} sx={{ mt: 2 }}>
                  {fields.map((field, index) => {
                    return (
                      <React.Fragment key={field.id}>
                        <Grid
                          item
                          xs={fields[index].type === 'NOTARY_MAP' ? 6 : 12}
                        >
                          <Controller
                            name={`referenceData.${index}.refValue` as const}
                            control={control}
                            rules={{
                              required: t(
                                `manageEstateAd.fieldRequired`
                              ) as unknown as string,
                              validate: (value) =>
                                Number(value) !== 0 ||
                                (t(
                                  `manageEstateAd.isNotZero`
                                ) as unknown as string),
                            }}
                            render={({ field, fieldState: { error } }) => (
                              <TextField
                                fullWidth
                                type="number"
                                variant="filled"
                                error={!!error}
                                helperText={error?.message}
                                label={t(
                                  `dashboard.pages.tools.${fields[index].type}`,
                                  {
                                    alternativeId:
                                      fields[index].alternativeId !== '0'
                                        ? fields[index].alternativeId
                                        : '',
                                  }
                                )}
                                size="small"
                                {...field}
                                inputRef={field.ref}
                                InputProps={{
                                  endAdornment: <PercentIcon />,
                                }}
                              />
                            )}
                          />
                        </Grid>
                        {fields[index].type === 'NOTARY_MAP' ? (
                          <Grid item xs={6}>
                            <Controller
                              name={
                                `referenceData.${index}.alternativeId` as const
                              }
                              control={control}
                              rules={{
                                required: t(
                                  `manageEstateAd.fieldRequired`
                                ) as unknown as string,
                                validate: (value) =>
                                  Number(value) !== 0 ||
                                  (t(
                                    `manageEstateAd.isNotZero`
                                  ) as unknown as string),
                              }}
                              render={({ field, fieldState: { error } }) => (
                                <TextField
                                  fullWidth
                                  type="number"
                                  variant="filled"
                                  error={!!error}
                                  helperText={error?.message ?? ' '}
                                  label={t(
                                    `dashboard.pages.tools.${fields[index].type}`,
                                    {
                                      alternativeId:
                                        fields[index].alternativeId !== '0'
                                          ? fields[index].alternativeId
                                          : '',
                                    }
                                  )}
                                  size="small"
                                  {...field}
                                  inputRef={field.ref}
                                />
                              )}
                            />
                          </Grid>
                        ) : null}
                      </React.Fragment>
                    )
                  })}

                  {/* Percentage not saved in backend currently */}
                  {/*

                 <Grid item xs={12}>
                  <Controller
                    name={`percentage`}
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <TextField
                        fullWidth
                        type="number"
                        variant="filled"
                        label={t('dashboard.pages.tools.financing')}
                        disabled={true}
                        size="small"
                        {...field}
                        inputProps={{
                          maxLength: 13,
                          step: 1,
                          min: 1,
                          max: 100,
                        }}
                        InputProps={{
                          endAdornment: <PercentIcon />,
                        }}
                      />
                    )}
                  />
                </Grid> */}
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </Grid>
        <Grid item xs="auto">
          <Button
            fullWidth
            type="submit"
            variant="contained"
            sx={{
              px: '32px',
              py: '8px',
              maxWidth: 350,
              fontSize: '16px',
              fontWeight: 500,
              position: 'sticky',
              top: '112px',
              mt: { md: '10px' },
            }}
          >
            {t('dashboard.pages.tools.button')}
          </Button>
        </Grid>
      </Grid>
    )
  }

  return null
}
